/**
 * Auth Actions API Hooks
 *
 * Handles authentication actions like login, logout, registration
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"

import type { Token } from "@/client/types.gen"
import { api } from "@/services/api"
import { useAuthStore } from "@/stores/auth"
import type { User } from "@/types/auth"
import { unwrapStandardResponse } from "@/utils/apiResponse"
import { extractScopesFromToken } from "@/utils/jwt"

/**
 * Authentication actions hook
 * @param options - Optional configuration, including whether to use navigation
 */
export function useAuthActions(options?: { skipNavigation?: boolean }) {
  const queryClient = useQueryClient()
  const { clearAuth } = useAuthStore()

  // Only use navigate if we're not skipping navigation (used during auth initialization)
  const navigate = options?.skipNavigation ? null : useNavigate()

  const logout = useMutation({
    mutationFn: async () => {
      try {
        await api.auth.logoutApiV1AuthLogoutPost()
      } catch (error) {
        // Even if backend logout fails, clear local state
      }
    },
    onSuccess: () => {
      // Clear all cached data
      queryClient.clear()

      // Clear auth store
      clearAuth()

      // Redirect to login if navigate is available
      if (navigate) {
        navigate({ to: "/login" })
      } else {
        window.location.href = "/login"
      }
    },
    onError: () => {
      // Even on error, clear local state and redirect
      queryClient.clear()
      clearAuth()
      if (navigate) {
        navigate({ to: "/login" })
      } else {
        window.location.href = "/login"
      }
    },
  })

  const login = useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      const response = await api.auth.loginApiV1AuthLoginPost({
        formData: {
          username: credentials.email, // OAuth2 spec uses username field
          password: credentials.password,
          grant_type: "password", // OAuth2 requires grant_type
        },
      })
      return response
    },
    onSuccess: async (data: Token) => {
      // Store the tokens in the auth store
      const { setTokens, setUser, setScopes } = useAuthStore.getState()

      if (data?.access_token) {
        // Set the access token
        setTokens(data.access_token, data.refresh_token || null)

        // Extract and set scopes from token
        const scopes = extractScopesFromToken(data.access_token)
        console.log("[AUTH] Extracted scopes from JWT:", scopes)
        console.log("[AUTH] Scopes count:", scopes.length)
        setScopes(scopes)

        // Fetch current user data and normalise payload
        try {
          const userResponse = await api.auth.getCurrentUserApiV1AuthMeGet()
          const userData = unwrapStandardResponse<User>(userResponse)
          if (userData) {
            setUser(userData)
            queryClient.setQueryData(["auth", "currentUser"], userData)
          }
        } catch (error) {
          console.error("Failed to fetch user data:", error)
        }

        // Invalidate queries
        queryClient.invalidateQueries({ queryKey: ["auth", "currentUser"] })
      }

      // Navigate after successful login
      if (navigate) {
        navigate({ to: "/" })
      } else {
        window.location.href = "/"
      }
    },
  })

  const register = useMutation({
    mutationFn: async (userData: {
      email: string
      password: string
      full_name: string
    }) => {
      const response = await api.auth.registerApiV1AuthRegisterPost({
        requestBody: userData,
      })
      return unwrapStandardResponse(response)
    },
    onSuccess: async (_data, variables) => {
      // Auto-login after successful registration
      try {
        const loginResponse = await api.auth.loginApiV1AuthLoginPost({
          formData: {
            username: variables.email,
            password: variables.password,
            grant_type: "password",
          },
        })

        if (loginResponse?.access_token) {
          const { setTokens, setUser, setScopes } = useAuthStore.getState()

          // Set tokens
          setTokens(
            loginResponse.access_token,
            loginResponse.refresh_token || null,
          )

          // Extract and set scopes from token
          const scopes = extractScopesFromToken(loginResponse.access_token)
          setScopes(scopes)

          // Fetch current user data
          try {
            const userResponse = await api.auth.getCurrentUserApiV1AuthMeGet()
            const userData = unwrapStandardResponse<User>(userResponse)
            if (userData) {
              setUser(userData)
              queryClient.setQueryData(["auth", "currentUser"], userData)
            }
          } catch (error) {
            console.error(
              "Failed to fetch user data after registration:",
              error,
            )
          }

          // Invalidate queries and navigate to dashboard (which will show onboarding wizard)
          queryClient.invalidateQueries({ queryKey: ["auth", "currentUser"] })

          if (navigate) {
            navigate({ to: "/" })
          } else {
            window.location.href = "/"
          }
        }
      } catch (loginError) {
        // If auto-login fails, fall back to login page
        console.error("Auto-login after registration failed:", loginError)
        if (navigate) {
          navigate({
            to: "/login",
            search: { message: "Registration successful! Please login." },
          })
        } else {
          window.location.href =
            "/login?message=Registration+successful!+Please+login."
        }
      }
    },
  })

  const requestPasswordReset = useMutation({
    mutationFn: async (email: string) => {
      const response =
        await api.auth.requestPasswordResetApiV1AuthPasswordResetPost({
          requestBody: { email },
        })
      return unwrapStandardResponse(response)
    },
  })

  const resetPassword = useMutation({
    mutationFn: async (data: { token: string; new_password: string }) => {
      const response = await api.instance.post(
        "/api/v1/auth/password/reset/confirm",
        data,
      )
      return response.data
    },
    onSuccess: () => {
      if (navigate) {
        navigate({
          to: "/login",
          search: { message: "Password reset successful! Please login." },
        })
      } else {
        window.location.href =
          "/login?message=Password+reset+successful!+Please+login."
      }
    },
  })

  const refreshToken = useMutation({
    mutationFn: async (refreshTokenValue: string) => {
      const response = await api.auth.refreshTokenApiV1AuthRefreshPost({
        requestBody: { refresh_token: refreshTokenValue },
      })
      return response
    },
    onSuccess: async (data: Token) => {
      const { setTokens, setScopes, setUser } = useAuthStore.getState()

      if (data?.access_token) {
        setTokens(data.access_token, data.refresh_token ?? null)
        const scopes = extractScopesFromToken(data.access_token)
        setScopes(scopes)

        try {
          const userResponse = await api.auth.getCurrentUserApiV1AuthMeGet()
          const userData = unwrapStandardResponse<User>(userResponse)
          if (userData) {
            setUser(userData)
            queryClient.setQueryData(["auth", "currentUser"], userData)
          }
        } catch (error) {
          console.error("Failed to refresh user data:", error)
        }

        queryClient.invalidateQueries({ queryKey: ["auth", "currentUser"] })
      }
    },
    onError: () => {
      queryClient.clear()
      clearAuth()
    },
  })

  return {
    logout: logout.mutateAsync,
    login: login, // Return the mutation object, not just mutateAsync
    register: register, // Return the full mutation object so .mutate() works
    requestPasswordReset: requestPasswordReset.mutateAsync,
    resetPassword: resetPassword.mutateAsync,
    refreshToken: refreshToken, // Return the mutation object for refreshToken too

    // Loading states
    isLoggingOut: logout.isPending,
    isLoggingIn: login.isPending,
    isLoading: login.isPending, // Add isLoading for compatibility
    isRegistering: register.isPending,
    isRequestingPasswordReset: requestPasswordReset.isPending,
    isResettingPassword: resetPassword.isPending,
    isRefreshingToken: refreshToken.isPending,

    // Error states
    loginError: login.error,
    error: login.error, // Add error for compatibility
    registerError: register.error,
    logoutError: logout.error,
    passwordResetError: requestPasswordReset.error,
    resetPasswordError: resetPassword.error,
    refreshTokenError: refreshToken.error,
  }
}

/**
 * Hook to fetch current user data
 */
export function useCurrentUser() {
  const { isAuthenticated } = useAuthStore()

  return useQuery({
    queryKey: ["auth", "currentUser"],
    queryFn: async () => {
      const response = await api.auth.getCurrentUserApiV1AuthMeGet()
      return unwrapStandardResponse(response)
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  })
}

export default useAuthActions
